import type { CSSProperties, HTMLAttributes, ReactNode } from "react";

import { ADAPTER_GUIDE_MAX_WIDTH, ADAPTER_GUIDE_MIN_WIDTH } from "@/features/adapters/guideWidth";
import type { useAdapterGuideResize } from "@/features/adapters/useAdapterGuideResize";

type AsideProps = HTMLAttributes<HTMLElement> & { [dataAttribute: `data-${string}`]: string | undefined };

interface AdapterGuideFrameProps {
  resize: ReturnType<typeof useAdapterGuideResize>;
  /** Rendered beside the guide, such as the field tour. */
  before?: ReactNode;
  asideProps?: AsideProps;
  asideStyle?: CSSProperties;
  children: ReactNode;
}

/**
 * The adapter guide's resizable side panel. Shared by the guide and its loading state, so the panel takes its
 * remembered width straight away and the spinner shows inside it.
 */
export const AdapterGuideFrame = ({ resize, before, asideProps, asideStyle, children }: AdapterGuideFrameProps) => {
  const { guideWidth, guideLayoutStyle, guideOverlapsContent, resizeHandleProps } = resize;
  return (
    <div
      className="absolute inset-y-0 right-0 z-40 w-full lg:relative lg:inset-auto lg:h-full lg:w-[var(--adapter-guide-reserved-width)] lg:shrink-0"
      style={guideLayoutStyle}
    >
      {before}
      <aside
        {...asideProps}
        className={`absolute inset-y-0 right-0 flex w-full min-w-0 max-w-[28rem] flex-col overflow-hidden border-l border-border bg-card shadow-2xl lg:w-[var(--adapter-guide-width)] lg:max-w-[calc(100vw-4rem)] ${guideOverlapsContent ? "lg:shadow-2xl" : "lg:shadow-none"}`}
        style={asideStyle}
      >
        <div
          className="group absolute inset-y-0 left-0 z-50 hidden w-6 -translate-x-1/2 touch-none cursor-ew-resize items-center justify-center outline-none lg:flex"
          role="separator"
          aria-label="Resize Adapter guide"
          aria-orientation="vertical"
          aria-valuemin={ADAPTER_GUIDE_MIN_WIDTH}
          aria-valuemax={ADAPTER_GUIDE_MAX_WIDTH}
          aria-valuenow={guideWidth}
          aria-valuetext={`${guideWidth} pixels`}
          tabIndex={0}
          {...resizeHandleProps}
        >
          <span className="h-14 w-1 rounded-full bg-border transition-colors group-hover:bg-primary group-focus-visible:bg-primary group-focus-visible:ring-2 group-focus-visible:ring-ring" />
        </div>
        {children}
      </aside>
    </div>
  );
};
